import { EditAction, DeleteAction } from 'components/V2/ActionIcons';
import useDialog from 'components/V2/Dialog';
import { useTranslate } from 'hooks/useTranslate';
import EditRegion from './_Partials/createOrEdit';
import BasicTable from 'components/Table/basic';
import { useList } from 'hooks/useList';
import { useDelete } from 'hooks/useDelete';
import { useDispatch, useSelector } from 'react-redux';
import useToast from 'components/V2/Toast';
import { showSpinner } from 'store/spinnerOverlay/actions';
import { useAlertDialog } from 'components/V2/AlertDialog';
import { useInvalidate } from 'hooks/useInvalidate';
import { END_POINTS } from 'variables/endPoint';
import { IRegionResource } from 'interfaces/IRegion';
import { useEffect, useMemo, useState } from 'react';
import DeleteRegionConfirmation from './_Partials/deleteConfirmation';
import customColorScheme from 'utils/customColorScheme';
import _ from 'lodash';

const Region = () => {
  const dispatch = useDispatch();
  const t = useTranslate();
  const { colorScheme } = useSelector((state: any) => state.colorScheme);
  const { showToast } = useToast();
  const { openDialog, closeDialog } = useDialog();
  const { showAlert } = useAlertDialog();
  const [pageSize, setPageSize] = useState(60);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const { data: regions } = useList<IRegionResource>(END_POINTS.REGION, { page_size: pageSize, search_value: searchValue });
  const { mutate: deleteRegion } = useDelete(END_POINTS.REGION);
  const invalidate = useInvalidate();

  useEffect(() => {
    if (regions) {
      setTotalCount(regions.total);
    }
  }, [regions, pageSize, currentPage, searchValue]);

  const columns = useMemo(() => [
    { name: 'id', title: t('common.id') },
    { name: 'name', title: t('common.name') },
    { name: 'therapist_limit', title: t('region.therapist_limit') },
    { name: 'phc_worker_limit', title: t('region.phc_worker_limit') },
    { name: 'action', title: t('common.action') }
  ], [t]);

  const handleEdit = (regionData: IRegionResource) => {
    openDialog({
      title: t('region.edit.title'),
      content: <EditRegion regionData={regionData} />,
      props: { size: 'lg' }
    });
  };

  const handleDelete = async (regionId: number) => {
    if (regionId) {
      dispatch(showSpinner(true));
      deleteRegion(
        regionId,
        {
          onSuccess: async (res) => {
            dispatch(showSpinner(false));
            invalidate(END_POINTS.COUNTRY_LIMITATION);
            invalidate(END_POINTS.ADMIN);
            invalidate(END_POINTS.PHC_WORKERS);
            showToast({
              title: t('region.toast_title.delete'),
              message: t(res?.message),
              color: 'success'
            });
            closeDialog();
          },
          onError: () => {
            dispatch(showSpinner(false));
          }
        }
      );
    }
  };

  const showConfirmDelete = (regionId: number) => {
    showAlert({
      title: t('region.delete_confirmation.title'),
      message: t('region.delete_confirmation.message'),
      closeOnConfirm: false,
      onConfirm: () => {
        closeDialog();
        openDialog({
          title: t('region.delete_confirmation.title'),
          content: (
            <DeleteRegionConfirmation
              regionId={regionId}
              onConfirm={() => handleDelete(regionId)}
            />
          ),
          props: { size: 'lg' }
        });
      }
    });
  };

  const rows = useMemo(() =>
    (regions?.data || []).map((region) => {
      const action = (
        <>
          <EditAction onClick={() => handleEdit(region)} />
          <DeleteAction onClick={() => showConfirmDelete(region.id)} />
        </>
      );

      return {
        id: region.id,
        name: region.name,
        therapist_limit: region.therapist_limit,
        phc_worker_limit: region.phc_worker_limit,
        action
      };
    }),
    [regions, handleEdit, showConfirmDelete]
  );

  return (
    <>
      <BasicTable
        rows={rows}
        columns={columns}
        showSearch={true}
        showPagination={true}
        pageSize={pageSize}
        setPageSize={setPageSize}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalCount={totalCount}
        setSearchValue={setSearchValue}
      />
      { !_.isEmpty(colorScheme) && customColorScheme(colorScheme) }
    </>
  );
};

export default Region;
